import React from 'react';
import ReactSelect, { components } from 'react-select';
import PropTypes from 'prop-types';
import { Icon } from '@takecasper/acuity-design-system';

import {
  WrapperStyled,
  LabelStyled,
  MessageStyled,
  IconWrapper
} from './select.styles';
import theme from 'shared/css/theme';

const legacyStyles = {
  option: (provided, state) => {
    const {
      selectProps: { useSecondaryColor } = {},
      isSelected = {},
      isFocused = false
    } = state || {};

    let borderColor = 'transparent';
    if (isSelected && useSecondaryColor) {
      borderColor = theme.colors.secondary.default;
    } else if (isSelected) {
      borderColor = theme.colors.primary.default;
    }

    let backgroundColor = 'transparent';
    if (isSelected && useSecondaryColor) {
      backgroundColor = theme.colors.secondary.lightest;
    } else if (isSelected) {
      backgroundColor = theme.colors.primary.lightest;
    } else if (isFocused) {
      backgroundColor = theme.colors.grey.lighter;
    }

    let hoverBackgroundColor = theme.colors.grey.lighter;
    if (isSelected && useSecondaryColor) {
      hoverBackgroundColor = theme.colors.secondary.lightest;
    } else if (isSelected) {
      hoverBackgroundColor = theme.colors.primary.lightest;
    }

    return {
      ...provided,
      borderLeft: `4px solid ${borderColor}`,
      backgroundColor,
      color: theme.colors.primary.darkest,
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
      transition: `all ${theme.transitions.fast} ease-in-out`,
      '&:active': {
        backgroundColor: 'transparent'
      },
      '&:hover': {
        backgroundColor: hoverBackgroundColor,
        cursor: 'pointer'
      }
    };
  },
  control: (provided, state) => {
    let backgroundColor = '#ffffff';
    if (state.isDisabled) {
      backgroundColor = theme.inputs.item.disabled.backgroundColor;
    }

    let cursor = 'pointer';
    if (state.selectProps.hideArrow) {
      cursor = 'default';
    }

    let hoverBorderColor = 'transparent';
    let hoverBoxShadow = theme.inputs.item.hover.boxShadow['default'];
    if (state.isFocused) {
      hoverBorderColor = theme.colors.primary.default;
      hoverBoxShadow = 'none';
    }

    return {
      ...provided,
      backgroundColor,
      border: theme.inputs.item.border,
      boxShadow: theme.inputs.item.boxShadow['default'],
      color: theme.inputs.item.color,
      cursor,
      fontSize: '1rem',
      height: theme.spacing(5),
      margin: `${theme.spacing(0.5)} 0`,
      minHeight: theme.spacing(5),
      outline: 'none',
      transition: `all ${theme.transitions.fast} ease-in-out`,
      '&:hover': {
        borderColor: hoverBorderColor,
        boxShadow: hoverBoxShadow
      },
      '&:focus-within': {
        borderColor: theme.colors.primary.default,
        boxShadow: theme.inputs.item.hover.boxShadow['default']
      }
    };
  },
  placeholder: provided => ({
    ...provided,
    color: theme.colors.grey.dark
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  menuList: (provided, state) => ({
    ...provided,
    padding: '0',
    maxHeight: `${state?.selectProps?.menuListHeight || '170'}px`,
    overflowX: 'hidden',
    overflowY: 'auto'
  }),
  menu: provided => ({
    ...provided,
    borderRadius: '0px',
    filter: 'drop-shadow(0px 4px 12px rgba(0, 0, 0, 0.15))'
  })
};

/**
 * Note:
 * This function is defined outside the scope of the Select component to ensure
 * that the focus on the contents does not jump to the top of the list when a
 * scroll event is triggered.
 */
const MenuList = ({ children, ...props }) => {
  return (
    <components.MenuList
      {...props}
      innerProps={{
        ...props.innerProps,
        onScroll: props.selectProps.handleScroll,
        'data-testid': `select-menu-list`
      }}
    >
      {children}
    </components.MenuList>
  );
};

/** Use the DS Icon for dropdown */
const DropdownIndicator = props => {
  return (
    <IconWrapper>
      <Icon name="dropdown" size="small" className="ds-text-neutrals-darker" />
    </IconWrapper>
  );
};

const Select = ({
  className,
  name,
  label,
  placeholder,
  selectedOption,
  hideArrow,
  options,
  handleChange,
  handleInputChange,
  isSearchable,
  error,
  errorMessage,
  errorPlaceholder,
  isDisabled,
  hideLabel,
  menuListHeight,
  useSecondaryColor,
  noOptionsMessage,
  handleScroll,
  onMenuOpen,
  onMenuClose,
  autoFocus,
  styleOverride,
  ...props
}) => {
  const message = error ? errorMessage : '';
  const errorPlaceholderMessage = errorPlaceholder ? 'message' : '';

  return (
    <WrapperStyled
      className={className}
      error={error}
      data-testid={props['data-testid'] || name}
      data-analytics-id={props['data-analytics-id']}
    >
      <LabelStyled htmlFor={name} hideLabel={hideLabel}>
        {label}
      </LabelStyled>
      <ReactSelect
        value={selectedOption}
        placeholder={placeholder}
        hideArrow={hideArrow}
        onChange={handleChange}
        onInputChange={handleInputChange}
        tabSelectsValue={false}
        options={options}
        styles={styleOverride || legacyStyles}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        aria-label={name}
        name={name}
        menuListHeight={menuListHeight}
        useSecondaryColor={useSecondaryColor}
        noOptionsMessage={() => noOptionsMessage}
        instanceId="custom"
        handleScroll={handleScroll}
        components={{ MenuList, DropdownIndicator }}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        autoFocus={autoFocus}
      />
      <MessageStyled
        error={error}
        hide={!Boolean(message)}
        data-testid="select-message"
      >
        {message || errorPlaceholderMessage}
      </MessageStyled>
    </WrapperStyled>
  );
};

Select.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  hideArrow: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  hideLabel: PropTypes.bool,
  menuListHeight: PropTypes.string,
  selectedOption: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  }),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ),
  handleChange: PropTypes.func,
  useSecondaryColor: PropTypes.bool,
  noOptionsMessage: PropTypes.string,
  handleScroll: PropTypes.func,
  components: PropTypes.object,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
  autoFocus: PropTypes.bool,
  styleOverride: PropTypes.object
};

Select.defaultProps = {
  hideArrow: false,
  isDisabled: false,
  options: [],
  isSearchable: true,
  hideLabel: false,
  handleChange: () => {},
  menuListHeight: '170',
  useSecondaryColor: false,
  noOptionsMessage: 'No options',
  autoFocus: false
};

export default Select;
